import { Link, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Markdown from '/@/components/markdown';
import settings from '/@/core/settings';
import {
    type CheckCuitAvailabilityResponse,
    type RVAdvisorInvitationApi,
    useAdvisorRvInvitations,
    useCreateAdvisorRvInvitation,
} from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getGrowerIdentifier } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import { growerIdentifierAlertPrefix } from './grower-identifier';

const ALERT_BACKGROUND_COLORS: Record<'surface' | 'warning' | 'info', string> = {
    surface: '#F5F5F5',
    warning: '#FFE4CD',
    info: '#E7F8FF',
};

const CuitAlert = ({
    availabilityOutput,
    identifierValue,
    onInvitationSent,
}: {
    availabilityOutput: CheckCuitAvailabilityResponse;
    identifierValue: string;
    onInvitationSent: (invitation: RVAdvisorInvitationApi) => void;
}) => {
    const navigate = useNavigate();
    const isAdvisor = useIsValidatedAdvisor();
    const isRV = useIsValidatedRV();
    const growerIdentifier = useSelector(getGrowerIdentifier);
    const createAdvisorRvInvitationMutation = useCreateAdvisorRvInvitation();

    const data = availabilityOutput;
    const idPrefix = growerIdentifierAlertPrefix(growerIdentifier, identifierValue);

    type AlertConfig = {
        severity: 'surface' | 'warning' | 'info';
        title: string;
        message: string;
        button?: React.ReactNode;
    };

    const sendInvitation = useCallback(
        async (userNodeId: number) => {
            await createAdvisorRvInvitationMutation.trigger({ userNodeId });
            if (createAdvisorRvInvitationMutation.data) onInvitationSent(createAdvisorRvInvitationMutation.data);
        },
        [createAdvisorRvInvitationMutation, onInvitationSent]
    );

    const advisorsRvInvitationsQuery = useAdvisorRvInvitations();

    const cuitAlert = useMemo((): AlertConfig | null => {
        if (!data) return null;

        switch (data.status) {
            case 'available':
                return null;
            case 'assigned_to_you':
                return {
                    severity: 'surface',
                    title: data.userNode.name || '',
                    message: `${idPrefix} ${_('Este productor ya está asignado a tu cartera.')}`,
                    button: (
                        <Link
                            color='secondary'
                            component='button'
                            onClick={() =>
                                navigate(reverse('Growers.detail', { params: { growerNodeId: data.userNode.id } }))
                            }
                        >
                            {_('Ver productor')}
                        </Link>
                    ),
                };
            case 'assigned_to_another_rv':
                if (isAdvisor) {
                    const contactRv = data.contact;
                    if (contactRv) {
                        const advisorRvInvitation = advisorsRvInvitationsQuery.data?.results.find(
                            (invitation) => invitation.rv.id === contactRv.id
                        );
                        return {
                            severity: 'info',
                            title: _('Este productor está asignado a un RV'),
                            message: `${idPrefix} ${_('El productor está registrado y asignado al RV {{RVName}}. Necesitás enviar una solicitud de vinculación al RV para trabajar con este productor.', { RVName: contactRv.name })}`,
                            button: (
                                <Link
                                    color='secondary'
                                    component='button'
                                    onClick={() => sendInvitation(contactRv.id)}
                                    disabled={!!advisorRvInvitation}
                                    sx={
                                        advisorRvInvitation
                                            ? { cursor: 'default', pointerEvents: 'none', color: 'text.disabled' }
                                            : {}
                                    }
                                >
                                    {advisorRvInvitation
                                        ? _('Solicitud enviada')
                                        : _('Enviar solicitud de vinculación')}
                                </Link>
                            ),
                        };
                    }
                    return {
                        severity: 'warning',
                        title: _('Este productor está asignado a otro RV'),
                        message: `${idPrefix} ${_('El productor está registrado y asignado a un RV diferente. Comunicate con tu RV Premium para consultar la situación de este productor.')}`,
                    };
                }

                if (isRV) {
                    if (data.contact) {
                        return {
                            severity: 'warning',
                            title: _('Este productor está asignado a {{RVName}}', { RVName: data.contact.name }),
                            message: `${idPrefix} ${_('El productor está registrado y asignado a un RV diferente. Comunicate con **{{infoEmail}}** para consultar la situación de este productor.', { infoEmail: settings.app.infoEmail })}`,
                        };
                    }
                }
                return null;
            case 'assigned_to_another_advisor':
                if (isAdvisor) {
                    return {
                        severity: 'warning',
                        title: _('Este productor no está asignado a tu cartera'),
                        message: `${idPrefix} ${_(
                            'Este productor está asignado a otro usuario. Comunicate con tu RV Premium o enviá un correo a **{{infoEmail}}** para consultar la situación del productor.',
                            { infoEmail: settings.app.infoEmail }
                        )}`,
                    };
                }

                if (isRV) {
                    const contactAdvisor = data.contact;
                    if (contactAdvisor) {
                        const advisorRvInvitation = advisorsRvInvitationsQuery.data?.results.find(
                            (invitation) => invitation.advisor.id === contactAdvisor.id
                        );
                        return {
                            severity: 'info',
                            title: _('Este productor está asignado a un Asesor'),
                            message: `${idPrefix} ${_('El productor está registrado y asignado al **Asesor {{advisorName}}**. Necesitás enviar una solicitud de vinculación al Asesor para trabajar con este productor.', { advisorName: contactAdvisor.name })}`,
                            button: (
                                <Link
                                    color='secondary'
                                    component='button'
                                    onClick={() => sendInvitation(contactAdvisor.id)}
                                    disabled={!!advisorRvInvitation}
                                >
                                    {advisorRvInvitation
                                        ? _('Solicitud enviada')
                                        : _('Enviar solicitud de vinculación')}
                                </Link>
                            ),
                        };
                    }
                }

                return null;
            case 'assigned_to_another_advisor_with_another_rv':
                return {
                    severity: 'warning',
                    title: _('Este productor está asignado a un Asesor del RV {{RVName}}', {
                        RVName: data.contact.name,
                    }),
                    message: `${idPrefix} ${_('El productor está registrado y asignado al asesor del RV {{RVName}}. Comunicate con su RV Premium o enviá un correo a **{{infoEmail}}** para consultar la situación del productor.', { RVName: data.contact.name, infoEmail: settings.app.infoEmail })}`,
                };
            case 'assigned_to_your_rv':
                return {
                    severity: 'warning',
                    title: _('Este productor no está asignado a tu cartera'),
                    message: `${idPrefix} ${_('Este productor fue creado por tu RV pero está asignado otro usuario. Comunicate con {{RVName}} para resolver la situación.', { RVName: data.contact.name })}`,
                };
            case 'assigned_to_your_advisor':
                return {
                    severity: 'warning',
                    title:
                        growerIdentifier === 'email'
                            ? _('Correo asignado a su asesor')
                            : _('CUIT asignado a su asesor'),
                    message: `${idPrefix} ${_('El productor está registrado y asignado tu **Asesor {{advisorName}}** pero no está compartido con vos. Contactate con el para que cambie el segmento de este productor a **Premium**.', { advisorName: data.contact.name })}`,
                };
        }
    }, [
        data,
        idPrefix,
        growerIdentifier,
        isAdvisor,
        isRV,
        navigate,
        sendInvitation,
        advisorsRvInvitationsQuery.data?.results,
    ]);

    return (
        <Stack
            className='mp-mask'
            p={2}
            alignItems='start'
            width='100%'
            sx={{
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: ALERT_BACKGROUND_COLORS[cuitAlert?.severity ?? 'surface'],
                textAlign: 'start',
            }}
            spacing={0.5}
        >
            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                {cuitAlert?.title}
            </Typography>
            {cuitAlert?.message && (
                <Markdown
                    components={{
                        strong: ({ children }) => (
                            <Typography variant='body2' color='primary.mui.inactive' component='span' fontWeight='bold'>
                                {children}
                            </Typography>
                        ),
                        p: ({ children }) => (
                            <Typography variant='body2' color='primary.mui.inactive'>
                                {children}
                            </Typography>
                        ),
                    }}
                >
                    {cuitAlert?.message}
                </Markdown>
            )}

            {cuitAlert?.button}
        </Stack>
    );
};

export default CuitAlert;
